import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db, auth } from "../firebase";
import {
  doc,
  getDoc,
  updateDoc,
  onSnapshot,
  arrayUnion,
  arrayRemove,
  query,
  collection,
  where,
  writeBatch,
  addDoc,
  Timestamp,
  orderBy,
  serverTimestamp,
  limit,
} from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { updateStreak } from "../utils/streakUtils";

function FocusRoomPage() {
  const { roomId } = useParams();
  const [room, setRoom] = useState(null);
  const [workingUsers, setWorkingUsers] = useState([]);
  const [participantsData, setParticipantsData] = useState([]);
  const [timerMode, setTimerMode] = useState("chronometer");
  const [duration, setDuration] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [localElapsed, setLocalElapsed] = useState(0);
  const [lastStartTime, setLastStartTime] = useState(null);
  const [error, setError] = useState("");
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [chatText, setChatText] = useState("");
  const chatBoxRef = useRef(null);
  const navigate = useNavigate();
  const currentUser = auth.currentUser;
  const isInitialLoadMessages = useRef(true); // Mesajlar için ilk yükleme
  const isInitialLoadNotifications = useRef(true); // Bildirimler için ilk yükleme

  useEffect(() => {
    if (!currentUser) {
      navigate("/auth");
      return;
    }

    if (!roomId) {
      navigate("/study-buddies");
      return;
    }

    const roomRef = doc(db, "focusRooms", roomId);
    const unsubscribeRoom = onSnapshot(
      roomRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setRoom(data);
          setIsRunning(data.isRunning);
          setTimerMode(data.mode || "chronometer");
          if (!data.isRunning) {
            setLocalElapsed(data.elapsed || 0);
          } else if (data.lastStartTime) {
            const serverStartSeconds = data.lastStartTime.seconds || data.lastStartTime._seconds;
            if (serverStartSeconds) {
              const nowSeconds = Math.floor(Date.now() / 1000);
              const diff = nowSeconds - serverStartSeconds;
              setLocalElapsed((data.elapsed || 0) + (diff > 0 ? diff : 0));
            }
          }
          setLastStartTime(data.lastStartTime);
        } else {
          navigate("/study-buddies");
        }
      },
      (err) => {
        setError(`Oda yüklenirken hata: ${err.message}`);
        toast.error(`Oda yüklenirken hata: ${err.message}`);
      }
    );

    const workingQuery = query(
      collection(db, "workingUsers"),
      where("active", "==", true)
    );
    const unsubscribeUsers = onSnapshot(
      workingQuery,
      (snapshot) => {
        const users = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setWorkingUsers(users);
      },
      (err) => {
        setError(`Çalışan kullanıcılar yüklenirken hata: ${err.message}`);
        toast.error(`Çalışan kullanıcılar yüklenirken hata: ${err.message}`);
      }
    );

    return () => {
      unsubscribeRoom();
      unsubscribeUsers();
    };
  }, [roomId, currentUser, navigate]);

  useEffect(() => {
    if (!room?.participants) return;

    const fetchParticipants = async () => {
      try {
        const participantsInfo = await Promise.all(
          room.participants.map(async (uid) => {
            const userRef = doc(db, "users", uid);
            const userSnap = await getDoc(userRef);
            return {
              uid,
              username: userSnap.exists()
                ? userSnap.data().username
                : "Bilinmeyen",
            };
          })
        );
        setParticipantsData(participantsInfo);
      } catch (err) {
        setError(`Katılımcılar yüklenirken hata: ${err.message}`);
        toast.error(`Katılımcılar yüklenirken hata: ${err.message}`);
      }
    };
    fetchParticipants();
  }, [room]);

  useEffect(() => {
    let intervalId;
    if (isRunning) {
      intervalId = setInterval(() => {
        setLocalElapsed((prev) => prev + 1);
      }, 1000);
    } else {
      if (intervalId) clearInterval(intervalId);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isRunning]);

  // Sohbet mesajlarını dinle (son 100 mesaj)
  useEffect(() => {
    if (!roomId || !currentUser) return;

    const messagesRef = collection(db, "focusRoomChats", roomId, "messages");
    const q = query(messagesRef, orderBy("timestamp", "desc"), limit(100));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const newMessages = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .reverse();
        setMessages(newMessages);
        // İlk yüklemede bildirim gönderme
        if (!isInitialLoadMessages.current) {
          // Yeni mesajlar için bildirim
          snapshot.docChanges().forEach((change) => {
            if (change.type === "added") {
              const msg = change.doc.data();
              if (msg.from !== currentUser.uid) {
                toast.info(`${msg.username}: ${msg.text}`, { autoClose: 3000 });
              }
            }
          });
        }
        setTimeout(() => {
          if (chatBoxRef.current && isChatOpen) {
            chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
          }
        }, 100);
        isInitialLoadMessages.current = false;
      },
      (err) => {
        setError(`Sohbet mesajları yüklenirken hata: ${err.message}`);
        toast.error(`Sohbet mesajları yüklenirken hata: ${err.message}`);
      }
    );
    return () => {
      unsubscribe();
      isInitialLoadMessages.current = true;
    };
  }, [roomId, isChatOpen, currentUser]);

  // Bildirimleri dinle (sadece "save" için)
  useEffect(() => {
    if (!roomId || !currentUser) return;

    const notificationsRef = collection(db, "focusRooms", roomId, "notifications");
    const q = query(notificationsRef, orderBy("timestamp"));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        // İlk yüklemede bildirim gönderme
        if (!isInitialLoadNotifications.current) {
          snapshot.docChanges().forEach((change) => {
            if (change.type === "added") {
              const { type, message } = change.doc.data();
              if (type === "save") {
                toast.info(message, { autoClose: 3000 });
              }
            }
          });
        }
        isInitialLoadNotifications.current = false;
      },
      (err) => {
        setError(`Bildirimler yüklenirken hata: ${err.message}`);
        toast.error(`Bildirimler yüklenirken hata: ${err.message}`);
      }
    );
    return () => {
      unsubscribe();
      isInitialLoadNotifications.current = true;
    };
  }, [roomId, currentUser]);

  const sendNotification = async (type, message) => {
    try {
      const notificationsRef = collection(db, "focusRooms", roomId, "notifications");
      await addDoc(notificationsRef, {
        type,
        message,
        timestamp: serverTimestamp(),
      });
    } catch (err) {
      setError(`Bildirim gönderilirken hata: ${err.message}`);
      toast.error(`Bildirim gönderilirken hata: ${err.message}`);
    }
  };

  const handleInvite = async (userId) => {
    if (!room || room.owner !== currentUser?.uid) return;
    try {
      const roomRef = doc(db, "focusRooms", roomId);
      await updateDoc(roomRef, {
        invitedUsers: arrayUnion(userId),
      });
      toast.success("Davet gönderildi!");
    } catch (err) {
      setError(`Davet gönderilirken hata: ${err.message}`);
      toast.error(`Davet gönderilirken hata: ${err.message}`);
    }
  };

  const handleAcceptInvite = async () => {
    try {
      const roomRef = doc(db, "focusRooms", roomId);
      await updateDoc(roomRef, {
        participants: arrayUnion(currentUser.uid),
        invitedUsers: arrayRemove(currentUser.uid),
      });
      toast.success("Davet kabul edildi!");
    } catch (err) {
      setError(`Davet kabul edilirken hata: ${err.message}`);
      toast.error(`Davet kabul edilirken hata: ${err.message}`);
    }
  };

  const handleTimerControl = async () => {
    if (room.owner !== currentUser?.uid) return;
    try {
      const roomRef = doc(db, "focusRooms", roomId);
      if (!isRunning) {
        await updateDoc(roomRef, {
          isRunning: true,
          mode: timerMode,
          duration: timerMode === "timer" ? duration * 60 : room.duration || 0,
          elapsed: localElapsed,
          lastStartTime: serverTimestamp(),
        });
      } else {
        await updateDoc(roomRef, {
          isRunning: false,
          elapsed: localElapsed,
        });
      }
      setIsRunning(!isRunning);
    } catch (err) {
      setError(`Zamanlayıcı kontrol edilirken hata: ${err.message}`);
      toast.error(`Zamanlayıcı kontrol edilirken hata: ${err.message}`);
    }
  };

  const handleTimerFinish = async () => {
    if (room.owner !== currentUser?.uid) return;
    try {
      const roomRef = doc(db, "focusRooms", roomId);
      await updateDoc(roomRef, {
        isRunning: false,
        elapsed: 0,
        duration: 0,
      });
      setIsRunning(false);
      setDuration(0);
    } catch (err) {
      setError(`Zamanlayıcı bitirilirken hata: ${err.message}`);
      toast.error(`Zamanlayıcı bitirilirken hata: ${err.message}`);
    }
  };

  const handleSaveToStudyTime = async () => {
    if (!room || room.owner !== currentUser?.uid) return;
    if (!room.elapsed || room.elapsed <= 0) {
      setError("Kaydedilecek süre sıfırdan büyük olmalı!");
      toast.error("Kaydedilecek süre sıfırdan büyük olmalı!");
      return;
    }

    try {
      const roomRef = doc(db, "focusRooms", roomId);
      await updateDoc(roomRef, {
        isRunning: false,
        elapsed: localElapsed,
      });
      setIsRunning(false);

      const batch = writeBatch(db);
      const now = new Date();
      const today = now.toISOString().split("T")[0];

      room.participants.forEach((userId) => {
        const entryRef = collection(db, "studyEntries");
        const entry = {
          duration: localElapsed,
          timestamp: Timestamp.fromDate(now),
          date: today,
          userId,
        };
        batch.set(doc(entryRef), entry);
      });

      await batch.commit();

      await Promise.all(
        room.participants.map(async (userId) => {
          await updateStreak(userId, "study");
        })
      );

      await sendNotification("save", "Durduruldu ve kaydedildi!");
    } catch (err) {
      setError(`Süre kaydedilirken hata: ${err.message}`);
      toast.error(`Süre kaydedilirken hata: ${err.message}`);
    }
  };

  const exitRoom = async () => {
    try {
      const roomRef = doc(db, "focusRooms", roomId);
      await updateDoc(roomRef, {
        participants: arrayRemove(currentUser.uid),
      });
      toast.success("Odadan çıktınız.");
      navigate("/study-buddies");
    } catch (err) {
      setError(`Odadan çıkılırken hata: ${err.message}`);
      toast.error(`Odadan çıkılırken hata: ${err.message}`);
    }
  };

  const sendMessage = async () => {
    if (!chatText.trim()) return;
    try {
      const messagesRef = collection(db, "focusRoomChats", roomId, "messages");
      const currentUserData = participantsData.find(
        (p) => p.uid === currentUser.uid
      );
      const username = currentUserData?.username || "Bilinmeyen";
      await addDoc(messagesRef, {
        text: chatText,
        from: currentUser.uid,
        username,
        timestamp: serverTimestamp(),
      });
      setChatText("");
      setTimeout(() => {
        if (chatBoxRef.current && isChatOpen) {
          chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
        }
      }, 100);
    } catch (err) {
      setError(`Mesaj gönderilirken hata: ${err.message}`);
      toast.error(`Mesaj gönderilirken hata: ${err.message}`);
    }
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  if (!room) {
    return (
      <motion.div
        style={containerStyle}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        Yükleniyor...
      </motion.div>
    );
  }

  return (
    <motion.div
      style={containerStyle}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.h2
        style={headerStyle}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.5 }}
      >
        {room.name}
      </motion.h2>
      <motion.p
        style={ownerStyle}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        Oda Sahibi:{" "}
        <a href={`/user/${room.owner}`} style={usernameLinkStyle}>
          {room.ownerUsername}
        </a>
      </motion.p>

      {room.participants.includes(currentUser.uid) ? (
        <>
          <motion.div
            style={timerContainerStyle}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <h3 style={timerDisplayStyle}>
              {formatTime(
                room.mode === "timer"
                  ? room.duration - localElapsed
                  : localElapsed
              )}
            </h3>
            {room.owner === currentUser.uid && (
              <motion.div
                style={controlBoxStyle}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                {!isRunning && (
                  <>
                    <label style={labelStyle}>
                      Mod:
                      <motion.select
                        value={timerMode}
                        onChange={(e) => setTimerMode(e.target.value)}
                        style={selectStyle}
                        whileFocus={{ borderColor: "#4caf50" }}
                      >
                        <option value="chronometer">Kronometre</option>
                        <option value="timer">Geri Sayım</option>
                      </motion.select>
                    </label>
                    {timerMode === "timer" && (
                      <label style={labelStyle}>
                        Süre (dakika):
                        <motion.input
                          type="number"
                          value={duration}
                          onChange={(e) => setDuration(Number(e.target.value))}
                          min="1"
                          style={inputStyle}
                          whileFocus={{ borderColor: "#4caf50" }}
                        />
                      </label>
                    )}
                  </>
                )}
                <div style={buttonGroupStyle}>
                  <motion.button
                    style={buttonStyle}
                    onClick={handleTimerControl}
                    whileHover={{ scale: 1.05, backgroundColor: "#388e3c" }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {isRunning
                      ? "Durdur"
                      : room.elapsed > 0
                      ? "Devam Et"
                      : "Başlat"}
                  </motion.button>
                  {(isRunning || room.elapsed > 0) && (
                    <motion.button
                      style={finishButtonStyle}
                      onClick={handleTimerFinish}
                      whileHover={{ scale: 1.05, backgroundColor: "#d32f2f" }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Bitir
                    </motion.button>
                  )}
                  {room.elapsed > 0 && (
                    <motion.button
                      style={saveButtonStyle}
                      onClick={handleSaveToStudyTime}
                      whileHover={{ scale: 1.05, backgroundColor: "#1976d2" }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Saatine Kaydet
                    </motion.button>
                  )}
                </div>
              </motion.div>
            )}
          </motion.div>

          <motion.div
            style={sectionStyle}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            <h3 style={subHeaderStyle}>Katılımcılar</h3>
            <ul style={listStyle}>
              <AnimatePresence>
                {participantsData.map((participant, index) => (
                  <motion.li
                    key={participant.uid}
                    style={listItemStyle}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.1, duration: 0.3 }}
                  >
                    <a
                      href={`/user/${participant.uid}`}
                      style={usernameLinkStyle}
                    >
                      {participant.username}
                    </a>
                  </motion.li>
                ))}
              </AnimatePresence>
            </ul>
            <motion.button
              style={buttonStyle}
              onClick={exitRoom}
              whileHover={{ scale: 1.05, backgroundColor: "#388e3c" }}
              whileTap={{ scale: 0.95 }}
            >
              Odadan Çık
            </motion.button>
          </motion.div>

          {room.owner === currentUser.uid && !isRunning && (
            <motion.div
              style={sectionStyle}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              <h3 style={subHeaderStyle}>Çalışanları Davet Et</h3>
              <ul style={listStyle}>
                <AnimatePresence>
                  {workingUsers.map((user, index) => (
                    <motion.li
                      key={user.id}
                      style={listItemStyle}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ delay: index * 0.1, duration: 0.3 }}
                    >
                      <a href={`/user/${user.id}`} style={usernameLinkStyle}>
                        {user.username}
                      </a>
                      {!room.participants.includes(user.id) &&
                        !(room.invitedUsers || []).includes(user.id) && (
                          <motion.button
                            style={buttonStyle}
                            onClick={() => handleInvite(user.id)}
                            whileHover={{
                              scale: 1.05,
                              backgroundColor: "#388e3c",
                            }}
                            whileTap={{ scale: 0.95 }}
                          >
                            Davet Et
                          </motion.button>
                        )}
                    </motion.li>
                  ))}
                </AnimatePresence>
              </ul>
            </motion.div>
          )}

          {/* Sohbet Butonu */}
          <motion.button
            style={chatToggleButtonStyle}
            onClick={() => setIsChatOpen(!isChatOpen)}
            whileHover={{ scale: 1.05, backgroundColor: "#00acc1" }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <span style={chatButtonTextStyle}>
              {isChatOpen ? "Sohbeti Kapat" : "💬 Sohbet"}
            </span>
          </motion.button>

          {/* Sohbet Paneli */}
          <AnimatePresence>
            {isChatOpen && (
              <motion.div
                style={chatPanelStyle}
                initial={{ x: "100%", opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: "100%", opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <motion.h3
                  style={chatHeaderStyle}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  Oda Sohbeti
                </motion.h3>
                <motion.div
                  style={chatBoxStyle}
                  ref={chatBoxRef}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <AnimatePresence>
                    {messages.map((msg, index) => (
                      <motion.div
                        key={msg.id}
                        style={
                          msg.from === currentUser.uid
                            ? myMsgStyle
                            : otherMsgStyle
                        }
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05, duration: 0.3 }}
                      >
                        <span style={msgUsernameStyle}>
                          {msg.username}
                        </span>
                        <span style={msgTextStyle}>{msg.text}</span>
                        <span style={msgTimestampStyle}>
                          {msg.timestamp?.toDate().toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </motion.div>
                <motion.div
                  style={chatInputContainerStyle}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.input
                    type="text"
                    value={chatText}
                    onChange={(e) => setChatText(e.target.value)}
                    placeholder="Mesaj yaz..."
                    style={chatInputStyle}
                    onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                    whileFocus={{ borderColor: "#4caf50" }}
                  />
                  <motion.button
                    onClick={sendMessage}
                    style={sendButtonStyle}
                    whileHover={{ scale: 1.05, backgroundColor: "#388e3c" }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Gönder
                  </motion.button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      ) : (
        <motion.button
          style={buttonStyle}
          onClick={handleAcceptInvite}
          whileHover={{ scale: 1.05, backgroundColor: "#388e3c" }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Daveti Kabul Et
        </motion.button>
      )}
      <AnimatePresence>
        {error && (
          <motion.p
            style={errorTextStyle}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
          >
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// Stiller (değişmedi)
const containerStyle = {
  padding: "20px",
  background: "linear-gradient(135deg, #0f2027, #203a43, #2c5364)",
  minHeight: "100vh",
  color: "#f1f1f1",
  fontFamily: "'Inter', sans-serif",
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  alignItems: "center",
  position: "relative",
};

const headerStyle = {
  textAlign: "center",
  fontSize: "28px",
  color: "#26c6da",
  textShadow: "none",
};

const ownerStyle = {
  textAlign: "center",
  fontSize: "16px",
  color: "#b0bec5",
};

const timerContainerStyle = {
  background: "rgba(255, 255, 255, 0.05)",
  padding: "20px",
  borderRadius: "12px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
  border: "1px solid rgba(38, 198, 218, 0.1)",
  textAlign: "center",
  width: "100%",
  maxWidth: "400px",
};

const timerDisplayStyle = {
  fontSize: "40px",
  fontWeight: "bold",
  color: "#26c6da",
  marginBottom: "20px",
  fontFamily: "'Roboto Mono', monospace",
  textShadow: "none",
};

const controlBoxStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  alignItems: "center",
};

const labelStyle = {
  color: "#b0bec5",
  fontSize: "14px",
  marginBottom: "5px",
};

const selectStyle = {
  padding: "8px",
  borderRadius: "6px",
  border: "1px solid #2a2a2a",
  background: "rgba(255, 255, 255, 0.15)",
  color: "#f1f1f1",
  fontSize: "14px",
  margin: "5px",
};

const inputStyle = {
  padding: "8px",
  width: "80px",
  borderRadius: "6px",
  border: "1px solid #2a2a2a",
  background: "rgba(255, 255, 255, 0.15)",
  color: "#f1f1f1",
  fontSize: "14px",
  textAlign: "center",
};

const buttonGroupStyle = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  justifyContent: "center",
};

const buttonStyle = {
  padding: "8px 16px",
  background: "linear-gradient(135deg, #4caf50, #388e3c)",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "600",
  minWidth: "100px",
};

const finishButtonStyle = {
  padding: "8px 16px",
  background: "linear-gradient(135deg, #ef5350, #d32f2f)",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "600",
  minWidth: "100px",
};

const saveButtonStyle = {
  padding: "8px 16px",
  background: "linear-gradient(135deg, #2196f3, #1976d2)",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "600",
  minWidth: "100px",
};

const sectionStyle = {
  background: "rgba(255, 255, 255, 0.05)",
  padding: "15px",
  borderRadius: "10px",
  marginTop: "20px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
  border: "1px solid rgba(38, 198, 218, 0.1)",
  width: "100%",
  maxWidth: "600px",
};

const subHeaderStyle = {
  fontSize: "20px",
  color: "#26c6da",
  marginBottom: "10px",
  textShadow: "none",
};

const listStyle = {
  listStyleType: "none",
  padding: 0,
  marginBottom: "15px",
};

const listItemStyle = {
  padding: "10px",
  background: "rgba(255, 255, 255, 0.05)",
  borderRadius: "6px",
  marginBottom: "8px",
  border: "1px solid rgba(38, 198, 218, 0.1)",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const usernameLinkStyle = {
  color: "#26c6da",
  textDecoration: "none",
  fontWeight: "600",
  fontSize: "14px",
};

const errorTextStyle = {
  marginTop: "15px",
  textAlign: "center",
  fontSize: "14px",
  color: "#ef5350",
};

// Sohbet Stilleri
const chatToggleButtonStyle = {
  position: "fixed",
  bottom: "20px",
  right: "20px",
  padding: "12px 20px",
  background: "linear-gradient(135deg, #26c6da, #00acc1)",
  color: "#fff",
  border: "none",
  borderRadius: "30px",
  cursor: "pointer",
  fontSize: "16px",
  fontWeight: "600",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
  zIndex: 1000,
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const chatButtonTextStyle = {
  fontSize: "16px",
};

const chatPanelStyle = {
  position: "fixed",
  bottom: "70px",
  right: "20px",
  width: "320px",
  height: "400px",
  background: "rgba(30, 40, 50, 0.95)",
  borderRadius: "12px",
  boxShadow: "0 6px 16px rgba(0, 0, 0, 0.4)",
  border: "1px solid rgba(38, 198, 218, 0.3)",
  display: "flex",
  flexDirection: "column",
  zIndex: 1000,
  overflow: "hidden",
  "@media (max-width: 480px)": {
    width: "90%",
    right: "5%",
    bottom: "60px",
  },
};

const chatHeaderStyle = {
  fontSize: "16px",
  color: "#26c6da",
  padding: "10px",
  textAlign: "center",
  borderBottom: "1px solid rgba(38, 198, 218, 0.1)",
};

const chatBoxStyle = {
  flex: 1,
  padding: "10px",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  scrollBehavior: "smooth",
};

const myMsgStyle = {
  alignSelf: "flex-end",
  background: "#4caf50",
  color: "#fff",
  padding: "8px 12px",
  borderRadius: "12px 12px 0 12px",
  maxWidth: "80%",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
};

const otherMsgStyle = {
  alignSelf: "flex-start",
  background: "#455a64",
  color: "#e0e0e0",
  padding: "8px 12px",
  borderRadius: "12px 12px 12px 0",
  maxWidth: "80%",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
};

const msgUsernameStyle = {
  fontSize: "12px",
  fontWeight: "600",
  color: "#26c6da",
};

const msgTextStyle = {
  fontSize: "14px",
};

const msgTimestampStyle = {
  fontSize: "10px",
  color: "#e0e0e0",
  alignSelf: "flex-end",
};

const chatInputContainerStyle = {
  display: "flex",
  gap: "8px",
  padding: "10px",
  background: "rgba(255, 255, 255, 0.05)",
  borderTop: "1px solid rgba(38, 198, 218, 0.1)",
};

const chatInputStyle = {
  flex: 1,
  padding: "8px",
  borderRadius: "6px",
  border: "1px solid #2a2a2a",
  background: "rgba(255, 255, 255, 0.15)",
  color: "#f1f1f1",
  fontSize: "14px",
  outline: "none",
};

const sendButtonStyle = {
  padding: "8px 16px",
  background: "linear-gradient(135deg, #4caf50, #388e3c)",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "600",
};

export default FocusRoomPage;